import React, {useState, useContext, useEffect} from 'react';
import Popup from '../common/components/Popup';
import TextInput from '../common/components/TextInput';
import NoImgUpload from '../../assests/img/no-img-upload.svg?react';
import {UserContext} from '../common/context/userContext';
import '../../assests/css/addcategory.scss';
import classNames from 'classnames';
import {categoryType} from './CategoryContent';
import {getImageAspectRatio} from '../common/functions/utils';
import {toast} from 'react-toastify';

interface addCategoryProps {
    showPopup: boolean,
    categoryToEdit?: categoryType,
    isEditable?: boolean,
    setShowPopup: (value: boolean) => void,
    addCategory: (category: newCategoryType) => Promise<boolean>,
    editCategory?: (category: categoryType) => Promise<boolean>,
}

export interface newCategoryType {
    categoryName: string,
    createdById: string,
    updatedById: string,
    uploadedImg: any,
    uploadedImgDataUrl: any,
}

function AddCategory(props: addCategoryProps) {
    const {showPopup, categoryToEdit, isEditable,
        setShowPopup, addCategory, editCategory} = props;
    const {data} = useContext(UserContext);

    const [
        newCategory,
        setNewCategory,
    ] = useState<newCategoryType>({
        categoryName: '',
        uploadedImg: null,
        uploadedImgDataUrl: null,
        createdById: '',
        updatedById: '',
    });
    const [
        showPreview,
        setShowPreview,
    ] = useState<boolean>(false);
    const [
        loading,
        setLoading,
    ] = useState<boolean>(false);

    useEffect(() => {
        if (isEditable && categoryToEdit) {
            setNewCategory({
                categoryName: categoryToEdit.categoryName,
                createdById: categoryToEdit.createdById,
                updatedById: categoryToEdit.updatedById,
                uploadedImg: null,
                uploadedImgDataUrl: categoryToEdit.fileUrl,
            });
            setShowPreview(true);
        }
    }, [
        isEditable, categoryToEdit,
    ]);

    const handleClose = () => {
        setNewCategory({categoryName: '', uploadedImg: null, uploadedImgDataUrl: null, createdById: '', updatedById: ''});
        setShowPopup(false);
        setShowPreview(false);
    };

    const handleOnChange = (text: string, inputId: keyof newCategoryType) => {
        setNewCategory((prevState) => {
            return {
                ...prevState,
                [inputId]: text,
                createdById: (!isEditable ?
                    data.userId.toString() :
                    prevState.createdById
                ),
                updatedById: (isEditable ?
                    data.userId.toString() :
                    prevState.updatedById),
            };
        });
    };

    const validateCategory = async () => {
        if (isEditable && categoryToEdit && editCategory) {
            setLoading(true);
            await editCategory({
                ...categoryToEdit,
                categoryName: newCategory.categoryName,
                fileUploaded: newCategory.uploadedImg,
                createdById: newCategory.createdById,
                updatedById: newCategory.updatedById,
            });
            setLoading(false);
        } else {
            if (newCategory.uploadedImg && newCategory.categoryName !== '') {
                setLoading(true);
                const canReset = await addCategory(newCategory);
                if (canReset) {
                    setNewCategory({categoryName: '', uploadedImg: null, uploadedImgDataUrl: null, createdById: '', updatedById: ''});
                    setShowPreview(false);
                }
                setLoading(false);
            }
        }
    };

    const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.currentTarget.files?.length === 1 &&
            e.currentTarget.files.item(0)) {
            const currentFile = e.currentTarget.files.item(0);
            if (currentFile?.type.includes('image/') &&
            currentFile.size/1000/1000 <= 1) {
                const output = document.getElementById('add-category-upload-preview') as HTMLImageElement;
                const fr = new FileReader();
                fr.onload = async function (event) {
                    if (output) {
                        await output.setAttribute('src', event.target?.result as string);
                        const ratio = getImageAspectRatio(
                            output.naturalWidth, output.naturalHeight);
                        const wr = output.naturalWidth / ratio;
                        const hr = output.naturalHeight / ratio;
                        if (wr === 1 && hr === 1) {
                            output.setAttribute('style', 'display: block');
                            setNewCategory((prevState) => {
                                return {
                                    ...prevState,
                                    uploadedImg: currentFile,
                                    uploadedImgDataUrl: event.target?.result,
                                };
                            });
                            setShowPreview(true);
                        } else {
                            output.setAttribute('src', newCategory.uploadedImgDataUrl);
                            output.setAttribute('style', `display: ${newCategory.uploadedImgDataUrl ? 'block' : 'none'}`);
                            toast.error('Make sure the uploaded icon is in 1:1 ratio & is less than 1 MB');
                        }
                    }
                }
                fr.readAsDataURL(currentFile);
            }
        }
        e.currentTarget.value = '';
    };

    const handleRemoveUploadedImg = () => {
        setNewCategory((prevState) => {
            return {
                ...prevState,
                uploadedImg: null,
                uploadedImgDataUrl: null,
            };
        });
        setShowPreview(false);
    };

    const handleEnableAddOrEdit = () => {
        if (isEditable && categoryToEdit) {
            return (
                newCategory.categoryName &&
                newCategory.uploadedImgDataUrl &&
                (newCategory.categoryName !== categoryToEdit.categoryName ||
                newCategory.uploadedImgDataUrl !== categoryToEdit.fileUrl)
            ) ? false : true;
        } else {
            return newCategory.categoryName === '' ||
            !newCategory.uploadedImg;
        }
    };
 
    return (
        <Popup
            show={showPopup}
            onClose={handleClose}
            title={!categoryToEdit && !isEditable ? 'Add Category' : 'Edit Category'}
            size='sm'
            primaryCtaText={!categoryToEdit && !isEditable ? 'Add' : 'Update'}
            disablePrimaryCta={handleEnableAddOrEdit()}
            primaryCtaOnClick={validateCategory}
            primaryCtaLoading={loading}
            secondaryCtaText='Cancel'
            secondaryCtaOnClick={handleClose}
            customClassName='add-category-popup'
        >
            <div className='mb-2'>
                <label className='form-label'>Category Name</label>
                <TextInput
                    type='text'
                    placeholder='Enter Category Name'
                    onChange={(text) => handleOnChange(text, 'categoryName')} text={newCategory.categoryName} style={{width: '100%'}}/>
            </div>
            <div className='mb-2'>
                <div className='add-category-upload-container'>
                        <div className={classNames('add-category-upload-input', !showPreview ? 'show' : '')}>
                            <input type='file' onChange={handleUpload} accept='image/*' title=''></input>
                            <div className='d-flex flex-column align-items-center'>
                                <NoImgUpload/>
                                <span className='add-category-upload-input-text'>Upload</span>
                            </div>
                        </div>
                        <div className={classNames('add-category-upload-content', !showPreview ? 'show' : '')}>
                            <div className='add-category-upload-header'>
                                Upload Image</div>
                            <div className='my-2'>Upload Icon in SVG or Image format</div>
                            <div>{`Make sure the uploaded icon is in 1:1 ratio & is less than 1 MB`}</div>
                        </div>
                        <div className={classNames('add-category-upload-input flex-column', showPreview ? 'show w-100' : '')}>
                            <img id='add-category-upload-preview'
                            src={newCategory.uploadedImgDataUrl}></img>
                            <div className='mt-2 d-flex alig-items-center gap-2'>
                                <span
                                    className='add-category-upload-input-text position-relative'
                                >
                                    <input type='file' onChange={handleUpload} accept='image/*' title=''></input>
                                    Change
                                </span>
                                <span
                                    className='add-category-upload-input-text'
                                    onClick={handleRemoveUploadedImg}
                                >
                                    Remove
                                </span>
                            </div>
                        </div>
                </div>
            </div>
        </Popup>
    );
}

export default AddCategory;
